import React, { useState, useEffect, useRef, useCallback } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import ArenaCover from "arena/components/ArenaCover";
import classNames from "classnames";
import Fade from "@mui/material/Fade";

const PREFIX = "ArenaCoverCarousel";

const classes = {
  first: `${PREFIX}-first`,
  last: `${PREFIX}-last`,
  item: `${PREFIX}-item`,
  dots: `${PREFIX}-dots`,
  dot: `${PREFIX}-dot`,
  dotActive: `${PREFIX}-dotActive`,
};

const Root = styled("div")(({ theme }) => ({
  position: "relative",
  height: "100vh",
  marginBottom: -100,
  [theme.breakpoints.up("sm")]: {
    height: "100vh",
  },

  [`&.${classes.first}`]: {
    marginTop: -54,
    [theme.breakpoints.up("sm")]: {
      marginTop: -64,
    },
  },

  [`&.${classes.last}`]: {
    marginBottom: 0,
  },

  [`& .${classes.item}`]: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },

  [`& .${classes.dots}`]: {
    zIndex: 10,
    position: "absolute",
    left: 10,
    right: 10,
    display: "flex",
    justifyContent: "center",
    bottom: 120,
  },

  [`& .${classes.dot}`]: {
    padding: 0,
    border: 0,
    width: 10,
    height: 10,
    borderRadius: 10,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.25)"
        : "rgba(0, 0, 0, 0.25)",
    margin: 10,
    cursor: "pointer",
  },

  [`& .${classes.dotActive}`]: {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.9)"
        : "rgba(0, 0, 0 , 0.9)",
  },
}));

function getVisibleSlides(activeIndex, total) {
  const slides = new Set([activeIndex]);
  slides.add((activeIndex + 1) % total);
  slides.add((activeIndex - 1 + total) % total);
  return slides;
}

function useRenderedSlides(slideIndex, total) {
  const rendered = useRef(new Set());

  const visible = getVisibleSlides(slideIndex, total);
  for (const i of visible) {
    rendered.current.add(i);
  }

  const shouldRender = useCallback(
    (index) => rendered.current.has(index),
    [slideIndex] // slideIndex triggers re-creation so callers get fresh results
  );

  return shouldRender;
}

export default function ArenaCoverCarousel({ arena, blocks, first, last }) {
  const [autoPlay, setAutoPlay] = useState(true);
  const [slideIndex, setSlideIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const shouldRender = useRenderedSlides(slideIndex, blocks.length);

  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Свайп влево - следующий слайд
      setSlideIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        return nextIndex >= blocks.length ? 0 : nextIndex;
      });
      setAutoPlay(false);
    } else if (isRightSwipe) {
      // Свайп вправо - предыдущий слайд
      setSlideIndex((prevIndex) => {
        const nextIndex = prevIndex - 1;
        return nextIndex < 0 ? blocks.length - 1 : nextIndex;
      });
      setAutoPlay(false);
    }
  };

  useEffect(() => {
    if (!autoPlay) return;
    if (!process.browser) return;
    if (blocks.length === 1) return;

    const timeout = setTimeout(() => {
      setSlideIndex((slideIndex) => {
        const nextIndex = slideIndex + 1;
        if (blocks[nextIndex]) {
          return nextIndex;
        }
        return 0;
      });
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [slideIndex, autoPlay, blocks]);

  const onChangeIndex = (event, slideIndex) => {
    setSlideIndex(slideIndex);
    setAutoPlay(false);
  };

  if (blocks.length === 1) {
    const [block] = blocks;
    return (
      <ArenaCover
        arena={arena}
        {...block.props}
        blockId={block.id}
        first={first}
        last={last}
      />
    );
  }

  return (
    <Root
      className={classNames({
        [classes.first]: first,
        [classes.last]: last,
      })}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {blocks.map((block, index) => (
        <Fade
          timeout={1000}
          appear={false}
          key={block.id}
          in={index === slideIndex}
        >
          <div className={classes.item}>
            {shouldRender(index) ? (
              <ArenaCover
                arena={arena}
                {...block.props}
                blockId={block.id}
                index={index + 1}
              />
            ) : null}
          </div>
        </Fade>
      ))}

      <div className={classes.dots}>
        {blocks.map((block, index) => {
          return (
            <button
              aria-label={`Slide ${index + 1}`}
              key={block.id}
              className={classNames({
                [classes.dot]: true,
                [classes.dotActive]: index === slideIndex,
              })}
              onClick={(event) => onChangeIndex(event, index)}
            />
          );
        })}
      </div>
    </Root>
  );
}

ArenaCoverCarousel.defaultProps = {
  blocks: [],
};

ArenaCoverCarousel.propTypes = {
  arena: PropTypes.object.isRequired,
  first: PropTypes.bool,
  last: PropTypes.bool,
  blocks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ),
};
